/**
 * 编辑器时间线数据工具：由 `FJVideoElement._timelineRenderCache.blocks`（或与
 * `getFaceTimelineRenderRects` 同形的块列表）整理为 FlexClip 编辑页 `timelineData` 形态的 Video 轨片段。
 */

import FJCoreConfig from '@src/core/FJCoreConfig';
import FJArchiveUtil from './FJArchiveUtil';
import FJRequirementsUtil from './FJRequirementsUtil';

const DEFAULT_SCENE_TRANSITION = {
    name: '',
    time: 1,
    type: 'MIXTURE',
    colors: [],
    easeType: '',
    isEnableSFX: false,
};

const DEFAULT_BACKGROUND_COLOR = '{"color":"#000000","blur":null}';
const BLUR_BACKGROUND_COLOR = '{"color":"#000000","blur":"GaussianBlurShader3"}';

const DEFAULT_ADJUST_FILTER = {
    exposure: 0,
    brightness: 0,
    temperature: 0,
    tint: 0,
    saturation: 0,
    contrast: 0,
    highlights: 0,
    shadows: 0,
    vignette: 0,
    active: true,
};

const DEFAULT_EDIT_FILTER = {
    blend: {
        type: '',
        intensity: 1,
        active: true,
    },
    chroma: {
        active: true,
        intensity: 0,
        smoothness: 0,
        color: '',
    },
    lut: {
        intensity: 1,
        lutId: '',
        isCustomLut: false,
        active: true,
    },
    stroke: {
        type: 'none',
    },
    blur: {
        type: 'none',
        intensity: 0,
        data: [],
        fade: {
            type: 'out',
            delay: 1,
            endDelay: 1,
            active: false,
        },
        isFeather: false,
    },
    version: 2,
};

function cloneDefaultEditFilter() {
    return JSON.parse(JSON.stringify(DEFAULT_EDIT_FILTER));
}

function defaultRecordAudio() {
    return { volume: 0.8, sourceName: '', id: null };
}

function defaultElements() {
    return {
        elementLayer: [{ id: -1, data: [] }],
        PIP_VIDEO: [],
    };
}

/** 与 `_computeDrawTimelineLayersNorm01` 中 role 一致；越前越优先作为「主画幅」relativeRect 来源 */
const PRIMARY_LAYER_ROLE_ORDER = [
    'subject_cover', // 主体 bbox：横↔竖跨画幅裁切 cover 满画布
    'single_cover', // 单人：人脸跟拍 cover 满画布
    'same_aspect', // 进画幅与出画幅一致，整帧等比铺满
    'blur_fit', // 无有效人脸策略：全片 contain 留边（可配模糊底）
    'dual_tiny_fit', // 16:9→9:16 双人小脸：合景单层 contain
    'single_fallback', // 单人 cover 失败：全片源对齐全画布
    'dual_split_top', // 双人竖分：上半区一人
    'dual_split_bottom', // 双人竖分：下半区一人
    'dual_split_fallback', // 竖分几何失败：全片源对齐全画布
    '169_single_full_fit_top', // 16:9 单人上下分条：上条全幅/居中 fit
    '169_single_face_bottom', // 16:9 单人上下分条：下条人脸 cover
];

function pickPrimaryLayer(layers) {
    if (!Array.isArray(layers) || layers.length === 0) {
        return null;
    }
    for (let r = 0; r < PRIMARY_LAYER_ROLE_ORDER.length; r++) {
        const role = PRIMARY_LAYER_ROLE_ORDER[r];
        const L = layers.find(l => l && l.role === role);
        if (L) {
            return L;
        }
    }
    return layers[layers.length - 1];
}

function normRectFallback(rect) {
    const x = Number(rect?.x);
    const y = Number(rect?.y);
    const w = Number(rect?.width);
    const h = Number(rect?.height);
    return {
        x: Number.isFinite(x) ? x : 0,
        y: Number.isFinite(y) ? y : 0,
        width: Number.isFinite(w) && w > 0 ? w : 1,
        height: Number.isFinite(h) && h > 0 ? h : 1,
    };
}

/**
 * 片源归一化裁剪 `sourceCrop` 与输出画布归一化绘制区 `renderRect`（与 `FJVideoElement` 时间线层一致）
 * → 编辑页主轨 `content.relativeRect`：等价于「整幅片源 1×1 放在画布上」时与当前 crop→dest 一致的轴对齐包围盒。
 */
function contentRelativeRectFromSourceCropAndRender(sourceCrop, renderRect) {
    const s = normRectFallback(sourceCrop);
    const r = normRectFallback(renderRect);
    const eps = 1e-9;
    const sw = Math.max(eps, s.width);
    const sh = Math.max(eps, s.height);
    const sx = r.width / sw;
    const sy = r.height / sh;
    return {
        x: r.x - s.x * sx,
        y: r.y - s.y * sy,
        width: sx,
        height: sy,
    };
}

function computeZoomScaleFromRelativeRect(rr) {
    const w = Number(rr?.width);
    const h = Number(rr?.height);
    if (!Number.isFinite(w) || w <= 1e-9 || !Number.isFinite(h) || h <= 1e-9) {
        return 1;
    }
    // contain 紧贴包围盒时，宽或高至少有一维贴齐画布（≈1）。旧逻辑用 1/w：竖片进横屏 pillarbox（w 小、h≈1）会把 zoom 拉到极大。
    // 与等比缩放一致：用 max(w,h) 反算，贴齐轴为 1 时 zoom≈1，避免编辑端重复放大。
    const m = Math.max(w, h);
    return m > 1e-9 ? 1 / m : 1;
}

/** 与 `FJVideoElement._computeDrawTimelineLayersNorm01` 一致：fit（contain / 留边）→ 缩小；fill（cover / 满幅等）→ 1 */
const ZOOM_CONTENT_FIT_ROLES = new Set(['blur_fit', 'dual_tiny_fit', '169_single_full_fit_top']);

function contentZoomScaleForLayerRole(role, relativeRect) {
    if (role && ZOOM_CONTENT_FIT_ROLES.has(String(role))) {
        return computeZoomScaleFromRelativeRect(relativeRect);
    }
    return 1;
}

/**
 * PIP_VIDEO `data`：`startTime`/`endTime`/`duration` 与对应 `block.startTime`/`block.endTime`（片源绝对秒）一致。
 */
function buildPipVideoData(seg, blockAbsStart, blockAbsEnd, sourceDur, fileInfo, pipId) {
    const rr = normRectFallback(seg?.renderRect);
    const cr = normRectFallback(seg?.sourceCrop);
    const bs = Number(blockAbsStart);
    const be = Number(blockAbsEnd);
    const blockDur = Number.isFinite(bs) && Number.isFinite(be) ? Math.max(0, be - bs) : 0;
    const fi = fileInfo && typeof fileInfo === 'object' ? { ...fileInfo } : {};
    return {
        name: '',
        opacity: 1,
        relativeRect: { ...rr },
        id: pipId,
        angle: 0,
        flipX: 1,
        flipY: 1,
        type: 'PIP_VIDEO',
        isLock: false,
        shadow: {
            color: '#000000',
            blur: 15,
            opacity: 40,
            offset: 5,
            direction: 48,
            enabled: false,
        },
        animationInName: 'None',
        animationLoopName: 'None',
        animationOutName: 'None',
        duration: blockDur,
        delay: 0,
        endDelay: 0,
        enterAnimationTime: 0,
        exitAnimationTime: 0,
        loopAnimationTime: 1,
        animationTextColor: '#ffffff',
        animationBackgroundColor: '#000000',
        customAnimationInfo: [],
        shapeFrameData: null,
        filter: cloneDefaultEditFilter(),
        adjustFilter: JSON.parse(JSON.stringify(DEFAULT_ADJUST_FILTER)),
        cropRect: { ...cr },
        maskType: 'RECT',
        fileInfo: fi,
        webSourceId: null,
        isTemplateSource: false,
        frameFlipX: 1,
        frameFlipY: 1,
        blendMode: 'normal',
        startTime: bs,
        endTime: be,
        volume: 0.8,
        speed: 1,
        videoDuration: sourceDur,
    };
}

/** 多 segment 时主轨占位：与编辑页 Color 板默认一致（仅下列字段） */
function defaultColorBoardContent() {
    return {
        zoomScale: 1,
        contentSize: {
            width: 100,
            height: 100,
        },
        fileInfo: {
            type: '',
            size: 0,
        },
        flipX: 1,
        flipY: 1,
        boardType: 'Color',
    };
}

function buildVideoContentBase({ relativeRect, zoomScale, contentSize, fileInfo, blockAbsStart, blockAbsEnd }) {
    const fi = fileInfo && typeof fileInfo === 'object' ? fileInfo : {};
    const cw = Number(contentSize?.width);
    const ch = Number(contentSize?.height);
    const sizeW = Number.isFinite(cw) && cw > 0 ? cw : 1280;
    const sizeH = Number.isFinite(ch) && ch > 0 ? ch : 720;
    const fileInfoOut =
        Object.keys(fi).length > 0
            ? { ...fi }
            : {
                  type: 'VIDEO',
                  size: { width: sizeW, height: sizeH },
                  name: '',
              };
    const bs = Number(blockAbsStart);
    const be = Number(blockAbsEnd);
    const blockDur = Number.isFinite(bs) && Number.isFinite(be) ? Math.max(0, be - bs) : 0;
    return {
        zoomScale,
        relativeRect: { ...relativeRect },
        contentSize: { width: sizeW, height: sizeH },
        fileInfo: fileInfoOut,
        flipX: 1,
        flipY: 1,
        volume: 0.8,
        speed: 1,
        rotation: 0,
        isSeparate: false,
        variableSpeeds: [],
        variableReserveType: 'none',
        motionTrackList: [],
        boardType: 'Video',
        startTime: bs,
        endTime: be,
        duration: blockDur,
    };
}

const FJEditAppUtil = {
    getEditAppUrl: function (url) {
        return url;
    },
    /**
     * @param {object} data - `{ ...timelineRenderCache, meta?: object }` 或 `{ blocks, clipStart, clipEnd, meta }`
     */
    buildEditAppBlocksData: function (data, meta, pid) {
        if (!data || typeof data !== 'object') {
            return data;
        }
        const cacheLike = {
            blocks: data.blocks,
            clipStart: data.clipStart,
            clipEnd: data.clipEnd,
            fw: data.fw,
            fh: data.fh,
        };
        if (!Array.isArray(cacheLike.blocks)) {
            return data;
        }
        return this.buildTimelineDataFromRenderCache(cacheLike, { ...meta, pid });
    },
    /**
     * @param {object} cacheLike - 与 `FJVideoElement._timelineRenderCache` 同形或子集：`blocks`、`clipStart`、`clipEnd`；可选 `fw`/`fh`
     * @param {object} meta
     * @param {number} meta.duration - 片源总时长（秒），写入每轨 `duration`；缺省用裁剪区间长度
     * @param {number} [meta.pid]
     * @param {number} [meta.version]
     * @param {string} [meta.id] - 写入 `fileInfo.id`（及缺省时的 `sourceName`）
     * @param {string} [meta.sourceName] - 与素材 id 对齐；缺省则用 `meta.id` 或 `meta.fileInfo.id`
     * @param {object} [meta.fileInfo]
     * @param {number} [meta.fw] [meta.fh] - 覆盖片源像素尺寸（否则用 cacheLike 或 fileInfo）
     * @param {object} [meta.transition]
     * @param {string|number} [meta.idSeed] - 生成片段 `id` 的前缀
     * @param {string} [meta.backgroundColor]
     * @returns {object[]}
     * @description segment 仅 1 个时视频写入 `content`；多于 1 个时写入 `elements.PIP_VIDEO`，`content` 为 Color 默认板。
     */
    buildTimelineDataFromRenderCache: function (cacheLike, meta = {}) {
        const blocks = Array.isArray(cacheLike?.blocks) ? cacheLike.blocks : [];
        const clipStart = Number.isFinite(Number(cacheLike?.clipStart)) ? Number(cacheLike.clipStart) : 0;
        const clipEnd = Number.isFinite(Number(cacheLike?.clipEnd)) ? Number(cacheLike.clipEnd) : clipStart;
        const clipLen = Math.max(0, clipEnd - clipStart);

        const sorted = [...blocks].sort((a, b) => (Number(a?.startTime) || 0) - (Number(b?.startTime) || 0));

        const metaDur = Number(meta.duration);
        const sourceDur = Number.isFinite(metaDur) && metaDur > 0 ? metaDur : clipLen;

        const fiMeta = meta.fileInfo && typeof meta.fileInfo === 'object' ? meta.fileInfo : {};
        const metaIdStr = meta.id != null && String(meta.id) !== '' ? String(meta.id) : '';
        const fw = Number.isFinite(Number(meta.fw))
            ? Number(meta.fw)
            : Number.isFinite(Number(cacheLike?.fw))
              ? Number(cacheLike.fw)
              : Number(fiMeta.size?.width) || 1280;
        const fh = Number.isFinite(Number(meta.fh))
            ? Number(meta.fh)
            : Number.isFinite(Number(cacheLike?.fh))
              ? Number(cacheLike.fh)
              : Number(fiMeta.size?.height) || 720;

        const version = meta.version != null ? meta.version : 7;
        const sourceName =
            meta.sourceName != null && meta.sourceName !== ''
                ? String(meta.sourceName)
                : metaIdStr !== ''
                  ? metaIdStr
                  : fiMeta.id != null
                    ? String(fiMeta.id)
                    : '';
        const fileInfo =
            Object.keys(fiMeta).length > 0
                ? { ...fiMeta }
                : {
                      id: sourceName,
                      type: 'VIDEO',
                      size: { width: fw, height: fh },
                      name: meta.fileName != null ? String(meta.fileName) : '',
                  };
        if (metaIdStr !== '') {
            fileInfo.id = metaIdStr;
        }

        const transition = meta.transition ? { ...meta.transition } : { ...DEFAULT_SCENE_TRANSITION };
        let backgroundColor = DEFAULT_BACKGROUND_COLOR;
        const out = [];
        for (let i = 0; i < sorted.length; i++) {
            const block = sorted[i];
            const blockAbsStart = Number(block.startTime);
            const blockAbsEnd = Number(block.endTime);
            const blockDur =
                Number.isFinite(blockAbsStart) && Number.isFinite(blockAbsEnd)
                    ? Math.max(0, blockAbsEnd - blockAbsStart)
                    : 0;

            const segments = Array.isArray(block.segments) ? block.segments : [];
            const segCount = segments.length;

            let content;
            let elements;
            let type = 'Video';
            if (segCount === 0) {
                content = defaultColorBoardContent();
                elements = defaultElements();
            } else if (segCount === 1) {
                const s0 = segments[0];
                const L1 = pickPrimaryLayer(block.layers);
                const rel = contentRelativeRectFromSourceCropAndRender(s0?.sourceCrop, s0?.renderRect);
                const zoomScale = contentZoomScaleForLayerRole(L1?.role, rel);
                backgroundColor = meta.isLightVideo ? DEFAULT_BACKGROUND_COLOR : BLUR_BACKGROUND_COLOR;
                content = buildVideoContentBase({
                    relativeRect: rel,
                    zoomScale,
                    contentSize: { width: fw, height: fh },
                    fileInfo,
                    blockAbsStart,
                    blockAbsEnd,
                });
                elements = defaultElements();
            } else {
                backgroundColor = DEFAULT_BACKGROUND_COLOR;
                content = defaultColorBoardContent();
                type = 'Color';
                const pipIds = [];
                const pipList = [];
                for (let j = 0; j < segCount; j++) {
                    const pipId = FJArchiveUtil.createId(true);
                    pipIds.push(pipId);
                    pipList.push({
                        id: pipId,
                        data: buildPipVideoData(segments[j], blockAbsStart, blockAbsEnd, sourceDur, fileInfo, pipId),
                    });
                }
                elements = {
                    elementLayer: [{ id: -1, data: pipIds }],
                    PIP_VIDEO: pipList,
                };
            }

            /** 0/1 个 segment：主轨 Video + 片源时长；>1：Color 占位 content + PIP + block 时长 */
            const sceneDuration = segCount > 1 ? blockDur : sourceDur;

            const scene = {
                id: FJArchiveUtil.createId(true),
                pid: meta.pid,
                version,
                duration: sceneDuration,
                startTime: blockAbsStart,
                endTime: blockAbsEnd,
                filterName: '',
                type,
                backgroundColor,
                textAnimation: [],
                logo: [],
                recordAudio: defaultRecordAudio(),
                content,
                sourceName,
                webSourceId: meta.webSourceId != null ? String(meta.webSourceId) : '',
                templateSource: meta.templateSource != null ? String(meta.templateSource) : '',
                previewName: meta.previewName != null ? String(meta.previewName) : '',
                transition,
                elements,
                adjustFilter: JSON.parse(JSON.stringify(DEFAULT_ADJUST_FILTER)),
                filter: cloneDefaultEditFilter(),
            };

            out.push(scene);
        }

        return out;
    },
    buildProjectData: function (editAppBlocksData, subtitleJson, fileMeta, pid) {
        return {
            id: pid,
            uid: FJRequirementsUtil.getUserId(),
            name: 'Untitled',
            videoSize: {
                width: FJCoreConfig.refSize.width,
                height: FJCoreConfig.refSize.height,
            },
            boards: editAppBlocksData,
            subtitle: subtitleJson,
            media_library: [
                {
                    fromType: 'LOCAL',
                    id: fileMeta.id,
                    name: fileMeta.name || fileMeta.id,
                    type: fileMeta.type,
                    fileInfo: {
                        name: fileMeta.name || fileMeta.id,
                        type: fileMeta.mimeType,
                        size: fileMeta.size,
                    },
                    customTemplateUrl: '',
                    size: {
                        width: fileMeta.width,
                        height: fileMeta.height,
                    },
                },
            ],
            folder_list: [
                // {
                //     id: fileMeta.id,
                //     parentId: '0',
                //     treeNodeType: 'file',
                //     createTime: Date.now(),
                // },
            ],
        };
    },
};

export default FJEditAppUtil;
